import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AllFeaturedPartnersStrip — continuous-loop marquee of every featured
 * AI tool, shown below the curated hero grid on the homepage.
 *
 * Why a marquee: on first paint the page otherwise feels "stalled" —
 * visitors don't realise there's more than the 3 curated hero tiles
 * below. A slow, continuous left-to-right (visually: tiles slide right
 * → left) scroll signals "there's a deeper directory here" without
 * requiring the user to actually scroll yet.
 *
 * Implementation:
 *   - The partner array is rendered TWICE inside an inline-flex track.
 *   - A CSS keyframe animates the track from `translateX(0)` to
 *     `translateX(-50%)` on a linear loop — the back half shows
 *     seamlessly where the front half just was, so the seam is
 *     invisible.
 *   - Speed scales with partner count (30 partners at 4s each = 120s
 *     loop, a leisurely full-rotation).
 *   - Pauses on hover so users can actually click a logo without
 *     chasing it.
 *   - Respects `prefers-reduced-motion` — falls back to a static
 *     horizontally-scrollable row for users who opted out of animations.
 *   - Edges fade-masked so tiles don't pop in/out visibly.
 */
export default function AllFeaturedPartnersStrip() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/affiliate-partners?limit=500`);
        if (!cancelled) setPartners(res.data.partners || []);
      } catch {
        // Non-critical UI — silently degrade.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Don't render below 4 partners — at that point the curated 3-card
  // hero grid is already showing the same partners and a strip would
  // feel redundant.
  if (!loaded || partners.length < 4) return null;

  // Roughly 4s per tile gives a readable but clearly-moving pace.
  // Clamped so 4-tile boards don't fly and 20+ tile boards don't crawl.
  const durationSec = Math.min(120, Math.max(30, partners.length * 4));

  return (
    <section
      className="container mx-auto px-4 sm:px-6 md:px-12 max-w-6xl pb-12 md:pb-20"
      data-testid="all-featured-partners-strip"
    >
      <div className="flex items-end justify-between mb-5 md:mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-1">
            Trusted by customers
          </p>
          <h3 className="text-base md:text-lg font-bold text-white">
            All featured AI tools
          </h3>
        </div>
        <button
          onClick={() => navigate('/partners')}
          className="text-xs text-[#00F0FF] hover:text-[#00C2CC] inline-flex items-center gap-1 font-semibold shrink-0"
          data-testid="all-featured-strip-view-all"
        >
          View all <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Edge-fade mask so tiles fade in/out rather than pop at the
          scroll region borders. `mask-image` is widely supported; the
          `-webkit-` prefix keeps older Safari / in-app browsers happy. */}
      <div
        className="relative overflow-hidden marquee-fade-mask"
        data-testid="all-featured-strip-row"
      >
        <div
          className="marquee-track flex gap-3 md:gap-4 w-max"
          style={{ animationDuration: `${durationSec}s` }}
        >
          {/* Render the list twice for a seamless -50% translate loop. */}
          {[...partners, ...partners].map((p, idx) => (
            <button
              key={`${p.slug}-${idx}`}
              onClick={() => navigate(`/featured/${p.slug}`)}
              className="group flex flex-col items-center gap-2 shrink-0 w-20 md:w-24"
              // Only the first copy is focusable — the cloned second
              // pass is purely decorative, so we hide it from the tab
              // order and assistive tech to avoid duplicate announcements.
              tabIndex={idx < partners.length ? 0 : -1}
              aria-hidden={idx < partners.length ? undefined : true}
              data-testid={idx < partners.length ? `all-featured-strip-${p.slug}` : undefined}
              aria-label={`View ${p.name} storefront`}
            >
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-neutral-900/60 border border-white/10 group-hover:border-[#00F0FF]/50 group-hover:bg-neutral-900 transition-all flex items-center justify-center p-3">
                {p.logo_url ? (
                  <img
                    src={p.logo_url}
                    alt={p.name}
                    className="w-full h-full object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-xs font-bold text-[#00F0FF]">
                    {p.name.charAt(0)}
                  </span>
                )}
              </div>
              <span className="text-[11px] text-neutral-500 group-hover:text-white transition-colors text-center leading-tight line-clamp-1 w-full">
                {p.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Scoped marquee animation + motion-reduce fallback. Inlined so
          the component stays self-contained (no need to touch the global
          Tailwind / index.css just for a 12-line keyframe). */}
      <style>{`
        .marquee-track {
          animation-name: marquee-scroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          will-change: transform;
        }
        .marquee-track:hover {
          animation-play-state: paused;
        }
        @keyframes marquee-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        /* Edge fade — the 32px transparent stops at each side dissolve
           the tiles so they don't hard-cut at the section border. */
        .marquee-fade-mask {
          -webkit-mask-image: linear-gradient(
            to right,
            transparent 0,
            black 32px,
            black calc(100% - 32px),
            transparent 100%
          );
          mask-image: linear-gradient(
            to right,
            transparent 0,
            black 32px,
            black calc(100% - 32px),
            transparent 100%
          );
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track {
            animation: none;
            flex-wrap: wrap;
            justify-content: center;
            width: auto;
          }
        }
      `}</style>
    </section>
  );
}
